import { cn } from '@/lib/utils';
import { 
    Pencil, 
    MessageSquarePlus, 
    FolderPlus, 
    EllipsisVertical,
    FolderTree, 
    Trash2, 
    FolderUp, 
    Folder 
} from 'lucide-react';
import { useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const itemClass =
  'rounded-lg px-2.5';

interface FolderData {
  id: string;
  name: string;
  level: number;
  parentId?: string | null;
}

interface Props {
  folder: FolderData;
  folders: FolderData[];
  onAddSubfolder: (parentId: string) => void;
  onAddChats: (folderId: string) => void;
  onRename: (folderId: string, currentName: string) => void;
  onDelete: (folderId: string) => void;
  onMove: (folderId: string, targetParentId: string | null) => void;
  isDescendantOf: (childId: string, parentId: string) => boolean;
  className?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function FolderOptions({
  folder,
  folders,
  onAddSubfolder,
  onAddChats,
  onRename,
  onDelete,
  onMove,
  isDescendantOf,
  className,
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onOpenChange = controlledOnOpenChange !== undefined ? controlledOnOpenChange : setInternalOpen;

  const canHaveSubfolder = folder.level < 3;

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          className={cn(
            'inline-flex items-center justify-center self-center size-7 rounded-md cursor-pointer [&_svg]:size-4',
            'text-muted-foreground hover:text-foreground hover:bg-foreground/10 data-[state=open]:bg-foreground/10 data-[state=open]:text-foreground transition-colors focus-visible:outline-hidden',
            className
          )}
        >
          <EllipsisVertical className="w-4 h-4" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" forceMount className="p-1.5 rounded-xl">
        {canHaveSubfolder && (
          <DropdownMenuItem
            className={itemClass}
            onClick={(e) => {
              e.stopPropagation();
              onAddSubfolder(folder.id);
            }}
           
          >
            <FolderPlus />
            Add project
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className={itemClass}
          onClick={(e) => {
            e.stopPropagation();
            onAddChats(folder.id);
          }}
         
        >
          <MessageSquarePlus />
          Add chats
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={itemClass}
          onClick={(e) => {
            e.stopPropagation();
            onRename(folder.id, folder.name);
          }}
         
        >
          <Pencil />
          Rename
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={itemClass}>
            <FolderTree />
            Move project
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-1.5 rounded-xl">
            {/* Move to root level */}
            {folder.parentId && (
              <DropdownMenuItem
                className={itemClass}
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(folder.id, null);
                }}
               
              >
                <FolderUp />
                Move to root
              </DropdownMenuItem>
            )}
            {/* Move to other folders */}
            {folders
              ?.filter(
                (f) =>
                  f.id !== folder.id && // Not itself
                  !isDescendantOf(folder.id, f.id) && // Not a descendant
                  f.level < 3 // Max nesting level
              )
              .map((targetFolder) => (
                <DropdownMenuItem
                  key={targetFolder.id}
                  className={itemClass}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMove(folder.id, targetFolder.id);
                  }}
                 
                  style={{
                    paddingLeft: `${10 + targetFolder.level * 16}px`
                  }}
                >
                  <Folder />
                  <span className="truncate min-w-0 flex-1 grow w-0">{targetFolder.name}</span>
                </DropdownMenuItem>
              ))}
            {folders?.filter(
              (f) =>
                f.id !== folder.id &&
                !isDescendantOf(folder.id, f.id) &&
                f.level < 3
            ).length === 0 &&
              !folder.parentId && (
                <DropdownMenuItem disabled className={itemClass}>
                  No available destinations
                </DropdownMenuItem>
              )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem
          variant="destructive"
          className="rounded-lg px-2.5"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(folder.id);
          }}
         
        >
          <Trash2 />
          Delete folder
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}